<template>
  <div class="story-filter-controls">
    <div v-if="hasSelections" class="selected-filter-bar">
      <div>
        <p class="filter-section-label">Selected Filters</p>
        <div class="selected-filter-chips">
          <button
            v-for="item in selectedItems"
            :key="`${item.type}-${item.slug}`"
            type="button"
            class="selected-filter-chip"
            :aria-label="`Remove ${item.label}`"
            @click="removeSelected(item)"
          >
            {{ item.label }}
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
      <button type="button" class="filter-clear-all" @click="clearAll">
        Clear All
      </button>
    </div>

    <section class="filter-section" aria-labelledby="story-filter-match-title">
      <div class="filter-section-heading">
        <div>
          <p class="filter-section-label">Matching Rule</p>
          <h3 id="story-filter-match-title">How should tags combine?</h3>
        </div>
        <div class="match-mode-toggle" aria-label="Tag matching rule">
          <button
            type="button"
            :class="{ active: filters.matchMode === 'all' }"
            :aria-pressed="(filters.matchMode === 'all').toString()"
            @click="setMatchMode('all')"
          >
            Match All
          </button>
          <button
            type="button"
            :class="{ active: filters.matchMode === 'any' }"
            :aria-pressed="(filters.matchMode === 'any').toString()"
            @click="setMatchMode('any')"
          >
            Match Any
          </button>
        </div>
      </div>
      <p class="filter-help-copy">
        {{ matchModeDescription }}
      </p>
    </section>

    <section class="filter-section" aria-labelledby="story-filter-type-title">
      <div class="filter-section-heading">
        <div>
          <p class="filter-section-label">Story Type</p>
          <h3 id="story-filter-type-title">Choose one or more types</h3>
        </div>
      </div>
      <div class="filter-pill-list">
        <button
          v-for="storyType in facetState.storyTypes"
          :key="storyType.slug"
          type="button"
          class="filter-pill filter-pill-type"
          :class="{ active: storyType.selected }"
          :aria-pressed="storyType.selected.toString()"
          :aria-label="optionLabel(storyType)"
          :disabled="!storyType.compatible"
          @click="toggleKind(storyType)"
        >
          <span>{{ storyType.label }}</span>
          <span class="filter-pill-count">{{ storyType.count }}</span>
        </button>
      </div>
    </section>

    <section class="filter-section" aria-labelledby="story-filter-tags-title">
      <div class="filter-section-heading filter-search-heading">
        <div>
          <p class="filter-section-label">Exact Tags</p>
          <h3 id="story-filter-tags-title">Browse all tags</h3>
        </div>
        <label class="tag-search-field" :for="`tag-search-${controlId}`">
          <span class="sr-only">Search all story tags</span>
          <v-icon aria-hidden="true" small>mdi-magnify</v-icon>
          <input
            :id="`tag-search-${controlId}`"
            v-model.trim="searchQuery"
            type="search"
            autocomplete="off"
            placeholder="Search all tags"
          >
        </label>
      </div>

      <div class="tag-group-list">
        <section
          v-for="group in visibleGroups"
          :key="group.slug"
          class="tag-group"
        >
          <button
            type="button"
            class="tag-group-toggle"
            :aria-expanded="groupIsOpen(group.slug).toString()"
            :aria-controls="`tag-group-${controlId}-${group.slug}`"
            @click="toggleGroup(group.slug)"
          >
            <span>{{ group.label }}</span>
            <span class="tag-group-summary">
              {{ group.tags.length }} {{ group.tags.length === 1 ? 'tag' : 'tags' }}
              <v-icon small aria-hidden="true">
                {{ groupIsOpen(group.slug) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </span>
          </button>
          <div
            v-show="groupIsOpen(group.slug)"
            :id="`tag-group-${controlId}-${group.slug}`"
            class="filter-pill-list tag-group-pills"
          >
            <button
              v-for="tag in group.tags"
              :key="tag.slug"
              type="button"
              class="filter-pill filter-pill-tag"
              :class="[
                `filter-pill-${tag.category}`,
                { active: tag.selected },
              ]"
              :aria-pressed="tag.selected.toString()"
              :aria-label="optionLabel(tag)"
              :disabled="!tag.compatible"
              @click="toggleTag(tag)"
            >
              <span>{{ tag.label }}</span>
              <span class="filter-pill-count">{{ tag.count }}</span>
            </button>
          </div>
        </section>
      </div>

      <p v-if="!visibleGroups.length" class="filter-empty-message" role="status">
        No tags match “{{ searchQuery }}”. Try another search or clear a selected filter.
      </p>
      <p v-else-if="searchQuery" class="filter-search-note" role="status">
        Unavailable matches remain visible with a zero count so you can see why they cannot be combined.
      </p>
    </section>
  </div>
</template>

<script>
import searchUtilities from '@/lib/reckonWaySearch.cjs';

const { getFacetState, normalizeFilterState } = searchUtilities;

const CATEGORY_GROUPS = [
  { slug: 'experience', label: 'Activities & Interests' },
  { slug: 'destination', label: 'Places' },
  { slug: 'theme', label: 'Culture & Occasions' },
  { slug: 'collection', label: 'Collections' },
];

let controlSequence = 0;

export default {
  name: 'StoryFilterControls',
  props: {
    value: {
      type: Object,
      required: true,
    },
    entries: {
      type: Array,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
  },
  data() {
    controlSequence += 1;
    return {
      controlId: controlSequence,
      openGroups: [],
      searchQuery: '',
    };
  },
  computed: {
    facetState() {
      return getFacetState(this.entries, this.tags, this.value);
    },
    filters() {
      return this.facetState.filters;
    },
    hasSelections() {
      return this.filters.kindSlugs.length > 0 || this.filters.tagSlugs.length > 0;
    },
    selectedItems() {
      const selectedTypes = this.facetState.storyTypes
        .filter((storyType) => storyType.selected)
        .map((storyType) => ({ ...storyType, type: 'kind' }));
      const selectedTags = this.facetState.tags
        .filter((tag) => tag.selected)
        .map((tag) => ({ ...tag, type: 'tag' }));

      return [...selectedTypes, ...selectedTags];
    },
    normalizedSearchQuery() {
      return this.searchQuery.toLowerCase();
    },
    visibleGroups() {
      return CATEGORY_GROUPS
        .map((group) => {
          const tags = this.facetState.tags
            .filter((tag) => tag.category === group.slug)
            .filter((tag) => {
              const matchesSearch = !this.normalizedSearchQuery
                || tag.label.toLowerCase().includes(this.normalizedSearchQuery)
                || tag.slug.toLowerCase().includes(this.normalizedSearchQuery);
              if (!matchesSearch) return false;

              return this.normalizedSearchQuery ? true : tag.compatible;
            });

          return { ...group, tags };
        })
        .filter((group) => group.tags.length > 0);
    },
    matchModeDescription() {
      return this.filters.matchMode === 'all'
        ? 'Every selected tag must appear on a story. Choices that cannot be combined are removed.'
        : 'A story may contain any selected tag. This broadens the result set.';
    },
  },
  watch: {
    normalizedSearchQuery(query) {
      if (query) this.openGroups = this.visibleGroups.map((group) => group.slug);
    },
  },
  methods: {
    nextValue(overrides = {}) {
      return normalizeFilterState({
        ...this.filters,
        ...overrides,
      }, this.facetState.storyTypes.map((storyType) => storyType.slug), this.tags.map((tag) => tag.slug));
    },
    emitValue(overrides) {
      this.$emit('input', this.nextValue(overrides));
    },
    setMatchMode(matchMode) {
      this.emitValue({ matchMode });
    },
    toggleKind(storyType) {
      if (!storyType.compatible) return;

      const kindSlugs = storyType.selected
        ? this.filters.kindSlugs.filter((slug) => slug !== storyType.slug)
        : [...this.filters.kindSlugs, storyType.slug];
      this.emitValue({ kindSlugs });
    },
    toggleTag(tag) {
      if (!tag.compatible) return;

      const tagSlugs = tag.selected
        ? this.filters.tagSlugs.filter((slug) => slug !== tag.slug)
        : [...this.filters.tagSlugs, tag.slug];
      this.emitValue({ tagSlugs });
    },
    removeSelected(item) {
      if (item.type === 'kind') this.toggleKind(item);
      else this.toggleTag(item);
    },
    clearAll() {
      this.searchQuery = '';
      this.openGroups = [];
      this.$emit('input', {
        kindSlugs: [],
        tagSlugs: [],
        matchMode: 'all',
      });
    },
    toggleGroup(groupSlug) {
      this.openGroups = this.openGroups.includes(groupSlug)
        ? this.openGroups.filter((slug) => slug !== groupSlug)
        : [...this.openGroups, groupSlug];
    },
    groupIsOpen(groupSlug) {
      return this.normalizedSearchQuery.length > 0 || this.openGroups.includes(groupSlug);
    },
    optionLabel(option) {
      const resultLabel = option.count === 1 ? 'story' : 'stories';
      const availability = option.compatible ? '' : ', unavailable with current filters';
      return `${option.label}, ${option.count} ${resultLabel}${availability}`;
    },
  },
};
</script>

<style scoped>
.story-filter-controls {
  display: grid;
  gap: 18px;
}

.filter-section,
.selected-filter-bar {
  background: #111;
  border: 1px solid #292929;
  border-radius: 10px;
  padding: 18px;
}

.selected-filter-bar,
.filter-section-heading {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
}

.filter-section-label {
  color: #e83b3b;
  font-family: "Oswald", sans-serif;
  font-size: .72rem;
  font-weight: 900;
  letter-spacing: .04em;
  margin: 0 0 5px;
  text-transform: uppercase;
}

.filter-section h3 {
  color: #fff;
  font-family: "Oswald", sans-serif;
  font-size: 1.05rem;
  margin: 0;
  text-transform: uppercase;
}

.selected-filter-chips,
.filter-pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-filter-chip,
.filter-clear-all,
.filter-pill,
.match-mode-toggle button {
  border: 1px solid #3b3b3b;
  color: #fff;
  cursor: pointer;
  font-family: "Oswald", sans-serif;
  font-weight: 800;
  text-transform: uppercase;
}

.selected-filter-chip {
  align-items: center;
  background: rgba(232, 59, 59, .14);
  border-color: #e83b3b;
  border-radius: 999px;
  display: inline-flex;
  font-size: .76rem;
  gap: 7px;
  padding: 6px 10px;
}

.selected-filter-chip span {
  font-size: 1rem;
  line-height: .8;
}

.filter-clear-all {
  background: transparent;
  border: 0;
  color: #d0d0d0;
  font-size: .75rem;
  padding: 5px;
  white-space: nowrap;
}

.filter-clear-all:hover,
.filter-clear-all:focus {
  color: #fff;
  text-decoration: underline;
}

.match-mode-toggle {
  background: #080808;
  border: 1px solid #333;
  border-radius: 999px;
  display: inline-flex;
  padding: 3px;
}

.match-mode-toggle button {
  background: transparent;
  border: 0;
  border-radius: 999px;
  font-size: .74rem;
  padding: 7px 11px;
}

.match-mode-toggle button.active {
  background: #e83b3b;
}

.filter-help-copy,
.filter-search-note,
.filter-empty-message {
  color: #aaa;
  font-size: .86rem;
  line-height: 1.5;
  margin: 10px 0 0;
}

.filter-pill-list {
  margin-top: 14px;
}

.filter-pill {
  align-items: center;
  background: #090909;
  border-radius: 999px;
  display: inline-flex;
  font-size: .76rem;
  gap: 8px;
  justify-content: space-between;
  padding: 7px 10px;
  transition: background 150ms ease, border-color 150ms ease, transform 150ms ease;
}

.filter-pill:hover:not(:disabled),
.filter-pill:focus:not(:disabled) {
  border-color: #e83b3b;
  transform: translateY(-1px);
}

.filter-pill.active {
  background: rgba(232, 59, 59, .16);
  border-color: #e83b3b;
}

.filter-pill:disabled {
  cursor: not-allowed;
  opacity: .42;
}

.filter-pill-count {
  align-items: center;
  background: #242424;
  border-radius: 999px;
  color: #cfcfcf;
  display: inline-flex;
  font-size: .68rem;
  justify-content: center;
  min-width: 23px;
  padding: 2px 6px;
}

.filter-pill-destination.active {
  background: rgba(57, 168, 255, .14);
  border-color: #39a8ff;
}

.filter-pill-theme.active {
  background: rgba(240, 177, 60, .14);
  border-color: #f0b13c;
}

.filter-pill-collection.active {
  background: rgba(125, 220, 135, .14);
  border-color: #7ddc87;
}

.filter-search-heading {
  align-items: center;
}

.tag-search-field {
  align-items: center;
  background: #080808;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  display: flex;
  min-width: min(310px, 45%);
  padding: 9px 12px;
}

.tag-search-field .v-icon {
  color: #aaa;
  margin-right: 8px;
}

.tag-search-field input {
  background: transparent;
  border: 0;
  color: #fff;
  font: inherit;
  min-width: 0;
  outline: 0;
  width: 100%;
}

.tag-search-field:focus-within {
  border-color: #e83b3b;
}

.tag-group-list {
  border-top: 1px solid #292929;
  margin-top: 16px;
}

.tag-group {
  border-bottom: 1px solid #292929;
}

.tag-group-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: .9rem;
  font-weight: 900;
  justify-content: space-between;
  padding: 15px 2px;
  text-align: left;
  text-transform: uppercase;
  width: 100%;
}

.tag-group-summary {
  align-items: center;
  color: #aaa;
  display: inline-flex;
  font-size: .72rem;
  gap: 5px;
}

.tag-group-summary .v-icon {
  color: inherit;
}

.tag-group-pills {
  margin: 0;
  padding: 0 0 16px;
}

.sr-only {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 620px) {
  .filter-section,
  .selected-filter-bar {
    padding: 15px;
  }

  .filter-section-heading,
  .selected-filter-bar {
    display: block;
  }

  .filter-clear-all,
  .match-mode-toggle,
  .tag-search-field {
    margin-top: 12px;
  }

  .tag-search-field {
    min-width: 0;
    width: 100%;
  }
}
</style>
